import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import {
  BotChallengeInteraction,
  subscribeBotChallengeInteraction,
  subscribeBotChallengeOutcome,
} from '@restworld/data-services';
import { Modal, ModalContent, Text } from '@restworld/ui-ds-base';
import { useAnalytics } from '@restworld/utils-common';

/**
 * Shows the Cloudflare Turnstile checkbox when Cloudflare wants a click before a protected call.
 *
 * The widgets are Managed: almost every visitor passes silently and never sees this. When Cloudflare is unsure,
 * `bot-challenge.ts` (data-services) drops the invisible widget and publishes the request; this host opens a
 * modal (bottom sheet on mobile) and mounts a visible widget in it. Closing it sends the call without a token.
 * Mount it once per app, inside the i18n provider. It also sends every outcome to analytics
 * (`bot_challenge_outcome`), which is how the share of people who meet the checkbox is measured.
 */
export function BotChallengeHost() {
  const { t } = useTranslation('common');
  const { trackEvent } = useAnalytics();
  const [interaction, setInteraction] = useState<BotChallengeInteraction | null>(null);
  const mounted = useRef<BotChallengeInteraction | null>(null);

  useEffect(() => subscribeBotChallengeInteraction(setInteraction), []);

  useEffect(
    () =>
      subscribeBotChallengeOutcome(({ action, outcome, elapsedMs, waited }) =>
        trackEvent('bot_challenge_outcome', { action, outcome, elapsed_ms: elapsedMs, waited })
      ),
    [trackEvent]
  );

  const slot = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || !interaction || mounted.current === interaction) return;
      mounted.current = interaction;
      interaction.mount(element);
    },
    [interaction]
  );

  const onOpenChange = (open: boolean) => {
    if (!open) interaction?.cancel();
  };

  return (
    <Modal open={!!interaction} onOpenChange={onOpenChange}>
      <ModalContent title={t('bot_challenge.title')} size="sm">
        <div className="flex flex-col gap-4 pb-2">
          <Text size={2}>
            {/* One text node: browser translation moves bare text into <font>, and removing the CV intro on its
                own while the modal closes crashed the page (RW-415). */}
            {`${interaction?.action === 'cv_upload' ? `${t('bot_challenge.cv_upload_intro')} ` : ''}${t(
              'bot_challenge.body'
            )}`}
          </Text>
          <div ref={slot} className="flex min-h-[65px] justify-center" data-testid="bot-challenge-slot" />
          <Text size={1} color="muted" align="center">
            {t('bot_challenge.footer')}{' '}
            {/* A plain anchor: ui-ds-base Link needs the app's NavigationContext, and this is an external page. */}
            <a href={t('bot_challenge.privacy_url')} target="_blank" rel="noopener noreferrer" className="underline">
              {t('bot_challenge.privacy_link')}
            </a>
          </Text>
        </div>
      </ModalContent>
    </Modal>
  );
}
